customElements.get('product-form') ||
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"].product-form__submit');
        this.submitButtonText = this.submitButton ? this.submitButton.querySelector('span') : null;
        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }
        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        const submitter =
          evt.submitter ||
          (document.activeElement &&
          document.activeElement.getAttribute('type') === 'submit' &&
          this.contains(document.activeElement)
            ? document.activeElement
            : null) ||
          this.submitButton;
        const isBuyNow = Boolean(submitter && submitter.hasAttribute('data-buy-now'));
        const activeButton = submitter || this.submitButton;

        if (!activeButton || activeButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        activeButton.setAttribute('aria-disabled', true);
        activeButton.classList.add('loading');
        const spinner = activeButton.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        // Buy Now skips cart drawer section rendering — clear cart, add this item, checkout.
        if (!isBuyNow && this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        // Ensure PDP quantity is present (input is form-associated, but be explicit).
        if (isBuyNow && !formData.get('quantity')) {
          const qtyInput =
            this.form.querySelector('[name="quantity"]') ||
            document.querySelector(`input.quantity__input[form="${this.form.id}"]`);
          if (qtyInput && qtyInput.value) formData.set('quantity', qtyInput.value);
        }

        config.body = formData;

        const root =
          (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

        const addPromise = isBuyNow
          ? this.prepareBuyNowCart(formData, root)
          : fetch(`${routes.cart_add_url}`, config).then((response) => response.json());

        addPromise
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              const soldOutMessage = activeButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              activeButton.setAttribute('aria-disabled', true);
              if (this.submitButtonText) this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            if (isBuyNow) {
              // Shared cart checkout path: re-applies promo codes + waits for BSS partner pricing.
              // Return the promise so finally can clear loading if checkout does not navigate.
              if (typeof window.moroccantanRunCheckout === 'function') {
                return window.moroccantanRunCheckout({
                  type: 'buy-now',
                  shippingProtectionIncluded: Boolean(response && response.__mtBuyNowIncludesSp),
                  cart: response && response.__mtBuyNowCart,
                  // Guests can't have B2B pricing — lets runCheckoutFlow skip the BSS
                  // pipeline and go straight to the native promo-code checkout.
                  guest: Boolean(response && response.__mtBuyNowGuest),
                });
              }
              window.location = `${root}checkout`;
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const bssReadyPromise =
              typeof window.mtWaitForBssCartPricing === 'function'
                ? window.mtWaitForBssCartPricing(formData.get('id'), 5000)
                : Promise.resolve(null);
            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            }
            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
            bssReadyPromise.then(() => {
              if (
                window.__mtLastSyncedCart &&
                typeof window.mtSyncCartTotals === 'function' &&
                typeof window.mtNextCartReq === 'function'
              ) {
                // Real req-sequence number — a raw Date.now() here would poison
                // lastAppliedReq and reject every later cart sync.
                window.mtSyncCartTotals(window.__mtLastSyncedCart, null, window.mtNextCartReq());
              }
            });
          })
          .catch((e) => {
            console.error(e);
            this.error = true;
          })
          .finally(() => {
            // Buy now: keep spinner only while leaving the page; if checkout aborts
            // (no B2B price / stuck), clear loading so the customer can retry.
            if (isBuyNow && !this.error && document.visibilityState === 'hidden') {
              CartPerformance.measureFromEvent('add:user-action', evt);
              return;
            }
            activeButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
            if (!this.error) activeButton.removeAttribute('aria-disabled');
            if (spinner) spinner.classList.add('hidden');
            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      /**
       * Buy now: checkout only this PDP line at the selected qty.
       * Preserves applied promo codes across cart/clear, unlocks BSS so partner
       * discounts can write before moroccantanRunCheckout navigates.
       */
      async prepareBuyNowCart(formData, root) {
        window.__mtBuyNow = window.__mtBuyNow || {};
        window.__mtBuyNow.t0 = performance.now();
        const log = window.moroccantanBuyNowLog || function () {};
        log('prepareBuyNowCart:start');
        // BSS only — do not call __mtActivateHeavyTier (staggers all heavy apps ~900ms
        // apart and can make Buy now take ~30–40s).
        // Arm before cart/clear|add so BSS PerformanceObserver bare CustomCartUpdate
        // events cannot start overlapping handleLoadCartData polls.
        if (typeof window.moroccantanArmBuyNowBssGuard === 'function') {
          window.moroccantanArmBuyNowBssGuard();
        }
        const bssReady =
          typeof window.__mtActivateBssForCheckout === 'function'
            ? window.__mtActivateBssForCheckout()
            : Promise.resolve(false);

        let promoCodes = [];
        try {
          const cart = await fetch(`${root}cart.js`, {
            headers: { Accept: 'application/json' },
          }).then((r) => r.json());
          const helpers = window.moroccantanCartDiscount;
          if (helpers && typeof helpers.extractDiscountCodesFromCart === 'function') {
            promoCodes = helpers.extractDiscountCodesFromCart(cart) || [];
          } else {
            promoCodes = (cart.discount_codes || [])
              .filter((d) => d && d.applicable !== false)
              .map((d) => d.code)
              .filter(Boolean);
          }
          const stored =
            helpers && typeof helpers.readStoredDiscountCodes === 'function'
              ? helpers.readStoredDiscountCodes()
              : [];
          promoCodes = [...new Set([...(promoCodes || []), ...(stored || [])].filter(Boolean))];
          if (helpers && typeof helpers.storeDiscountCodes === 'function' && promoCodes.length) {
            helpers.storeDiscountCodes(promoCodes);
          }
        } catch (e) {
          console.error(e);
        }
        log('promoCodes:extracted', promoCodes);

        await fetch(`${root}cart/clear.js`, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json',
          },
        });
        log('cart:cleared');

        // Product + shipping protection in ONE cart/add (avoids later
        // enforceShippingProtection cart.js + cart/add on buy-now). Respect the
        // customer's SP opt-out here so the line is never added (avoids the
        // stale-BSS-sync add→remove ordering hazard).
        const variantId = Number(formData.get('id'));
        const quantity = Number(formData.get('quantity') || 1) || 1;
        const items = [{ id: variantId, quantity }];
        const spDisabled =
          typeof window.moroccantanSpDisabled === 'function' &&
          window.moroccantanSpDisabled();
        const spId = spDisabled
          ? null
          : typeof window.moroccantanGetShippingProtectionVariantId === 'function'
            ? Number(window.moroccantanGetShippingProtectionVariantId())
            : Number(
                document.querySelector('cart-drawer')?.getAttribute('data-shipping-id') || 0
              );
        if (spId && spId !== variantId) {
          items.push({ id: spId, quantity: 1 });
        }
        const properties = {};
        formData.forEach((value, key) => {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match) properties[match[1]] = value;
        });
        if (Object.keys(properties).length) items[0].properties = properties;
        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) items[0].selling_plan = Number(sellingPlan);

        const addResponse = await fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items }),
        });
        const response = await addResponse.json();
        log('cart:added', { itemCount: response.item_count });
        if (response.status) return response;

        // Prime Shopify's discount session cookie (/discount/CODE) so the code
        // survives checkout creation on every vehicle — native form POST, BSS-built
        // checkout URL, or BSS's own redirect (cart-level codes don't reliably
        // transfer into BSS-created checkouts).
        const primeDiscount = promoCodes.length
          ? fetch(`${root}discount/${encodeURIComponent(promoCodes[0])}`, {
              credentials: 'same-origin',
            }).catch((e) => console.error(e))
          : Promise.resolve();

        // Promo reapply runs concurrently with the BSS cart sync below.
        const promoUpdate = promoCodes.length
          ? fetch(`${root}cart/update.js`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
              body: JSON.stringify({ discount: promoCodes.join(',') }),
            }).catch((e) => console.error(e))
          : Promise.resolve();

        // Mark so runCheckoutFlow can skip a second SP add on buy-now.
        response.__mtBuyNowIncludesSp = items.length > 1;

        // Quick Buy Now is retail-only: the button only renders for guests and
        // non-B2B customers (snippets/buy-buttons.liquid, professional-tag
        // convention). A rendered [data-buy-now][data-mt-b2b-customer] means a
        // B2B customer somehow got the button — those take the BSS pipeline.
        // Everyone else (including logged-in retail) skips the BSS partner-
        // pricing wait entirely and checks out natively with promo codes.
        const b2bBuyNow = Boolean(
          this.form.querySelector('[data-buy-now][data-mt-b2b-customer]')
        );
        response.__mtBuyNowGuest = !b2bBuyNow;

        // BSS load started in parallel with cart surgery. Always wait until cartSubject
        // is usable when B2B UI is on the page — racing ahead is the main flaky path.
        const needsBss =
          b2bBuyNow &&
          Boolean(
            document.querySelector(
              '[bss-b2b-main-product-form], .bss-b2b-cart-item-qty-table, .bss-b2b-qty-table-wrapper, [bss-b2b-product-price]'
            )
          );
        let syncedCart = null;
        if (needsBss) {
          await Promise.race([
            bssReady,
            new Promise((resolve) => setTimeout(resolve, 3000)),
          ]);
          log('bssReady:done', { needsBss });
          // Notify BSS of the new cart while the promo reapply is in flight —
          // lets BSS compute partner pricing before runCheckoutFlow's poll.
          if (typeof window.moroccantanBuyNowPostAddSync === 'function') {
            try {
              syncedCart = await window.moroccantanBuyNowPostAddSync();
            } catch (e) {
              console.error(e);
            }
          }
        }
        await Promise.all([promoUpdate, primeDiscount]);
        log('promos:reapplied');
        response.__mtBuyNowCart = syncedCart;

        return response;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;
        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) {
            this.submitButtonText.textContent = window.variantStrings.addToCart;
          }
        }

        const buyNow = this.querySelector('[data-buy-now]');
        if (buyNow) {
          if (disable) buyNow.setAttribute('disabled', 'disabled');
          else buyNow.removeAttribute('disabled');
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );

function refreshCartDrawer() {
  let i = document.querySelector('cart-drawer-items');
  const e = document.querySelector('cart-drawer');
  const r = document.querySelector('.cart__checkout-button');
  if (e) {
    e.classList.contains('is-empty') && e.classList.remove('is-empty');
    if (r) r.removeAttribute('disabled');
    const t = e.querySelector('.drawer__inner-empty');
    t && t.remove();
    const a = document.querySelector('.cart__contents');
    a && (a.style.display = 'block');
  }
  if (!i) {
    i = document.createElement('cart-drawer-items');
    const t = document.querySelector('.drawer__header');
    t && t.insertAdjacentElement('afterend', i);
  }
  const s = document.querySelector('cart-drawer-items');
  s && typeof s.onCartUpdate == 'function' && s.onCartUpdate();
  e && typeof e.open == 'function' && e.open();
}
